import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box, Typography, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Stack, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert, Popover,
  Divider
} from '@mui/material';
import * as XLSX from 'xlsx';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  Event as EventIcon,
  PersonAdd as PersonAddIcon,
  ContactPhone as ContactPhoneIcon,
  SupervisorAccount as GuardianIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

// Configuration - uses environment variable with fallback for development
// const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// const API_ENDPOINTS = {
//   registrations: `${API_BASE_URL}/api/registrations`
// };

const API_BASE_URL = ''; 

const API_ENDPOINTS = {
  registrations: '/api/registrations'
};

// Layout constants
const SIDEBAR_WIDTH = 300;
const MAX_INPUT_LENGTH = 255;
const MAX_PHONE_LENGTH = 20;
const MAX_EMAIL_LENGTH = 254;

// Dark System theme color palette
const COLORS = Object.freeze({
  bg: '#232226',
  sidebar: '#1F1E21',
  card: '#2B2A2E',
  cardHover: '#333236',
  accent: '#6C5DD3',
  accentHover: '#7c6fe0',
  border: 'rgba(255,255,255,0.08)',
  textPrimary: '#ffffff',
  textSecondary: '#8E8E93',
  success: '#34c759',
  error: '#ff453a',
  warning: '#ff9f0a'
});

// Initial Event Metadata - now just initial data, not frozen
const INITIAL_EVENT_METADATA = [
  {
    id: 'DCIRosewell26',
    title: 'Dance & Choreography Intensive ("DCI")',
    location: 'Rosewell, Georgia',
    date: 'June 8 - 13, 2026',
    options: [
      { value: 'Dancer', label: 'Dancer' },
      { value: 'Choreographer', label: 'Choreographer', subtext: '(by acceptance only)' }
    ]
  },
  {
    id: 'DCILawrence26',
    title: 'Dance & Choreography Intensive ("DCI")',
    location: 'Lawrence, Kansas',
    date: 'June 22 - 27, 2026',
    options: [
      { value: 'Dancer', label: 'Dancer' },
      { value: 'Choreographer', label: 'Choreographer', subtext: '(by acceptance only)' }
    ]
  },
  {
    id: 'DCICarlisle26',
    title: 'Dance & Choreography Intensive ("DCI")',
    location: 'Carlisle, Pennsylvania',
    date: 'July 27 - August 1, 2026',
    options: [
      { value: 'Dancer', label: 'Dancer' },
      { value: 'Choreographer', label: 'Choreographer', subtext: '(by acceptance only)' }
    ]
  },
  {
    id: 'PWKingwood26',
    title: 'Performance Workshop',
    location: 'Kingswood, Texas',
    date: 'June 5 - 7, 2026',
    options: [
      { value: 'Dancer', label: 'Dancer' }
    ]
  }
];

// Helper function to create empty registrant template
const createEmptyRegistrant = () => ({
  uniqueID: null,
  fullName: '',
  preferredName: '',
  dateOfBirth: '',
  gender: '',
  dancerEmail: '',
  dancerPhoneNumber: '',
  prounouns: '',
  // Guardian (flat fields)
  guardianFullName: '',
  guardianPhoneNumber: '',
  guardianEmail: '',
  // Emergency Contact (flat fields)
  emergencyContactName: '',
  emergencyContactPhoneNumber: '',
  emergencyContactRelationship: '',
  emergencyContactEmail: '',
  // Medical
  allergies: '',
  prescribedMedications: '',
  medicalConditions: '',
  physicianName: '',
  // Events
  DCIRosewell26: '',
  DCILawrence26: '',
  DCICarlisle26: '',
  PWKingwood26: ''
});

// Input sanitization utilities
const sanitizeInput = (value, maxLength = MAX_INPUT_LENGTH) => {
  if (value === null || value === undefined) return '';
  return String(value).trim().slice(0, maxLength);
};

const sanitizeEmail = (email) => {
  const sanitized = sanitizeInput(email, MAX_EMAIL_LENGTH).toLowerCase();
  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(sanitized) ? sanitized : email;
};

const sanitizePhone = (phone) => {
  return sanitizeInput(phone, MAX_PHONE_LENGTH).replace(/[^\d+\-() ]/g, '');
};

// Calculate age from date of birth with validation
const calculateAge = (dob) => {
  if (!dob) return '';
  try {
    const today = new Date();
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return '';
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age >= 0 && age < 150 ? age : '';
  } catch {
    return '';
  }
};

// Extract pronouns label from object or string safely
const getPronouns = (pronouns) => {
  if (!pronouns) return '';
  if (typeof pronouns === 'object' && pronouns !== null && pronouns.label) {
    return sanitizeInput(pronouns.label);
  }
  return sanitizeInput(pronouns);
};

// Reusable text field styling
const textFieldStyle = {
  '& .MuiInputBase-root': {
    color: COLORS.textPrimary,
    bgcolor: COLORS.bg,
    borderRadius: '8px',
    fontSize: '0.875rem',
  '&.Mui-disabled': {
      bgcolor: COLORS.bg, 
      WebkitTextFillColor: COLORS.textPrimary, 
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: COLORS.border, 
      },
    }
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: COLORS.textPrimary, 
    WebkitTextFillColor: COLORS.textPrimary, 
  },
  '& .MuiInputLabel-root': { color: COLORS.textSecondary, fontSize: '0.875rem' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.border },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.accent },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: COLORS.accent }
};

// RegistrantForm component defined OUTSIDE to prevent remounting on parent re-render
const RegistrantForm = ({ registrant, setRegistrant }) => {
  const handleChange = useCallback((field, value, sanitizer = sanitizeInput) => {
    setRegistrant(prev => ({ ...prev, [field]: sanitizer(value) }));
  }, [setRegistrant]);

  return (
    <Stack spacing={3} sx={{ width: '100%', px: 1 }}>
      {/* Participant Information */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.accent, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Participant Information
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Full Name"
              value={registrant?.fullName || ''}
              onChange={(e) => handleChange('fullName', e.target.value)}
              fullWidth
              required
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Preferred Name"
              value={registrant?.preferredName || ''}
              onChange={(e) => handleChange('preferredName', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Date of Birth"
              type="date"
              value={registrant?.dateOfBirth ? String(registrant.dateOfBirth).split('T')[0] : ''}
              onChange={(e) => handleChange('dateOfBirth', e.target.value, (v) => v)}
              fullWidth
              required
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={textFieldStyle}
            />
            <TextField
              label="Age"
              value={calculateAge(registrant?.dateOfBirth) || ''}
              fullWidth
              size="small"
              disabled
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Gender"
              value={registrant?.gender || ''}
              onChange={(e) => handleChange('gender', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 50 }}
              sx={textFieldStyle}
            />
            <TextField
              label="Pronouns"
              value={getPronouns(registrant?.prounouns) || ''}
              onChange={(e) => handleChange('prounouns', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 50 }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Dancer Email"
              type="email"
              value={registrant?.dancerEmail || ''}
              onChange={(e) => handleChange('dancerEmail', e.target.value, sanitizeEmail)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Dancer Phone"
              value={registrant?.dancerPhoneNumber || ''}
              onChange={(e) => handleChange('dancerPhoneNumber', e.target.value, sanitizePhone)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_PHONE_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>

      {/* Guardian Information (flat fields) */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.accent, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Guardian Information
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="Guardian Full Name"
            value={registrant?.guardianFullName || ''}
            onChange={(e) => handleChange('guardianFullName', e.target.value)}
            fullWidth
            size="small"
            inputProps={{ maxLength: MAX_INPUT_LENGTH }}
            sx={textFieldStyle}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Guardian Phone"
              value={registrant?.guardianPhoneNumber || ''}
              onChange={(e) => handleChange('guardianPhoneNumber', e.target.value, sanitizePhone)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_PHONE_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Guardian Email"
              type="email"
              value={registrant?.guardianEmail || ''}
              onChange={(e) => handleChange('guardianEmail', e.target.value, sanitizeEmail)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>

      {/* Emergency Contact (flat fields) */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.success, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Emergency Contact
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Contact Name"
              value={registrant?.emergencyContactName || ''}
              onChange={(e) => handleChange('emergencyContactName', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Relationship"
              value={registrant?.emergencyContactRelationship || ''}
              onChange={(e) => handleChange('emergencyContactRelationship', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 100 }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Phone"
              value={registrant?.emergencyContactPhoneNumber || ''}
              onChange={(e) => handleChange('emergencyContactPhoneNumber', e.target.value, sanitizePhone)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_PHONE_LENGTH }}
              sx={textFieldStyle}
            />
            <TextField
              label="Email"
              type="email"
              value={registrant?.emergencyContactEmail || ''}
              onChange={(e) => handleChange('emergencyContactEmail', e.target.value, sanitizeEmail)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_EMAIL_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>

      {/* Medical Information */}
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="subtitle2" sx={{ color: COLORS.error, mb: 2, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}>
          Medical Information
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Allergies"
              value={registrant?.allergies || ''}
              onChange={(e) => handleChange('allergies', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 500 }}
              sx={textFieldStyle}
            />
            <TextField
              label="Prescribed Medications"
              value={registrant?.prescribedMedications || ''}
              onChange={(e) => handleChange('prescribedMedications', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 500 }}
              sx={textFieldStyle}
            />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Medical Conditions"
              value={registrant?.medicalConditions || ''}
              onChange={(e) => handleChange('medicalConditions', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: 500 }}
              sx={textFieldStyle}
            />
            <TextField
              label="Physician Name"
              value={registrant?.physicianName || ''}
              onChange={(e) => handleChange('physicianName', e.target.value)}
              fullWidth
              size="small"
              inputProps={{ maxLength: MAX_INPUT_LENGTH }}
              sx={textFieldStyle}
            />
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

const EventsDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // DYNAMIC EVENT METADATA STATE
  const [eventMetadata] = useState(INITIAL_EVENT_METADATA);

  // State for registrations data
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);

  // Dialog state management
  const [addRegistrantDialogOpen, setAddRegistrantDialogOpen] = useState(false);
  const [newRegistrant, setNewRegistrant] = useState(createEmptyRegistrant);

  const [editRegistrantDialogOpen, setEditRegistrantDialogOpen] = useState(false);
  const [editingRegistrant, setEditingRegistrant] = useState(null);


  // Popover state for contact details
  const [contactAnchorEl, setContactAnchorEl] = useState(null);
  const [activeContact, setActiveContact] = useState(null);
  const [contactType, setContactType] = useState(null);

  // Load registrations from API with abort controller for cleanup
  const fetchRegistrations = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');

      const response = await fetch(API_ENDPOINTS.registrations, { 
        signal,
        method: 'GET', 
            headers: {
                'Content-Type': 'application/json',
                
                'Authorization': `Bearer ${token}` 
            }
       });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const data = await response.json();

      // Validate response is an array
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received');
      }

      // Handle both formats for backward compatibility
      if (data.length > 0 && data[0]?.dancers_list) {
        const allRegistrants = data.flatMap(event =>
          (event.dancers_list || []).map(dancer => ({ ...dancer, _eventId: event.id }))
        );
        setRegistrations(allRegistrants);
      } else {
        setRegistrations(data);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(err.message);
        console.error('Error fetching registrations:', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch registrations on mount with cleanup
  useEffect(() => {
    const controller = new AbortController();
    fetchRegistrations(controller.signal);
    return () => controller.abort();
  }, [fetchRegistrations]);

  // Handle navigation state (restore selected event when coming back)
  useEffect(() => {
    if (location.state?.selectedEventId) {
      const eventId = location.state.selectedEventId;
      // Validate the event ID exists
      if (eventMetadata.some(e => e.id === eventId)) {
        setSelectedEventId(eventId);
      }
    }
  }, [location.state, eventMetadata]);

  // Navigate to registrant detail page
//   const handleRegistrantClick = useCallback((registrant) => {
//     if (registrant?.id && selectedEventId) {
//       navigate(`/admin/events/${encodeURIComponent(selectedEventId)}/dancer/${encodeURIComponent(registrant.id)}`);
//     }
//   }, [navigate, selectedEventId]);
const handleRegistrantClick = useCallback((registrant) => {
  if (registrant?.unique_id && selectedEventId) {
    navigate(`/admin/events/${encodeURIComponent(selectedEventId)}/dancer/${encodeURIComponent(registrant.unique_id)}`);
  } else {
    console.error("Missing unique_id or selectedEventId", { registrant, selectedEventId });
  }
}, [navigate, selectedEventId]);

  const selectedEvent = useMemo(() => {
    return eventMetadata.find(e => e.id === selectedEventId) || null;
  }, [eventMetadata, selectedEventId]);

  const filteredRegistrations = useMemo(() => {
    if (!selectedEventId) return [];
    return registrations.filter(reg =>
      reg && reg[selectedEventId] && String(reg[selectedEventId]).trim() !== ''
    );
  }, [registrations, selectedEventId]);

  const getEventCount = useCallback((eventId) => {
    return registrations.filter(reg =>
      reg && reg[eventId] && String(reg[eventId]).trim() !== ''
    ).length;
  }, [registrations]);


  // Add registrant with validation
//   const handleAddRegistrant = useCallback(async () => {
//     if (!newRegistrant.fullName?.trim() || !newRegistrant.dateOfBirth) {
//       return;
//     }

//     const registrantWithId = {
//       ...newRegistrant,
//       id: Date.now(),
//       fullName: sanitizeInput(newRegistrant.fullName),
//       // Set the selected event role
//       [selectedEventId]: 'Dancer'
//     };

//     try {
//       setRegistrations(prev => [...prev, registrantWithId]);
//       setAddRegistrantDialogOpen(false);
//       setNewRegistrant(createEmptyRegistrant());
//     } catch (err) {
//       console.error('Error adding registrant:', err);
//       setError('Failed to add registrant');
//     }
//   }, [newRegistrant, selectedEventId]);

  const handleAddRegistrant = useCallback(async () => {
    if (!newRegistrant.fullName?.trim() || !newRegistrant.dateOfBirth) {
        return;
    }

    try {
        setLoading(true);
        
        const token = localStorage.getItem('token'); 

        const initResponse = await fetch(`${API_BASE_URL}/api/initialize-participant`, {
        method: 'POST',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ 
            email: newRegistrant.participantEmail || null 
        })
        });

        if (!initResponse.ok) {
        const errorData = await initResponse.json();
        throw new Error(errorData.message || 'Failed to initialize participant');
        }

        const { uniqueId } = await initResponse.json();

        const fullRegistrantData = {
        ...newRegistrant,
        unique_id: uniqueId,
        fullName: sanitizeInput(newRegistrant.fullName),
        [selectedEventId]: 'Dancer' 
        };

        const updateResponse = await fetch(`${API_BASE_URL}/api/registrations/${uniqueId}`, {
        method: 'PUT',
        headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(fullRegistrantData)
        });

        if (!updateResponse.ok) {
        throw new Error('Failed to save registrant details');
        }

        setRegistrations(prev => [fullRegistrantData, ...prev]);
        setAddRegistrantDialogOpen(false);
        setNewRegistrant(createEmptyRegistrant());
        
    } catch (err) {
        console.error('Error adding registrant:', err);
        setError(err.message || 'Failed to add registrant to database');
    } finally {
        setLoading(false);
    }
    }, [newRegistrant, selectedEventId]);

  // Edit registrant with validation
  // const handleEditRegistrant = useCallback(async () => {
  //   if (!editingRegistrant?.fullName?.trim()) {
  //     return;
  //   }

  //   const registrantId = editingRegistrant.unique_id || editingRegistrant.id;
  //   if (!registrantId) {
  //     return;
  //   }

  //   try {
  //     const response = await fetch(`${API_ENDPOINTS.registrations}/${registrantId}`, {
  //       method: 'PUT',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify(editingRegistrant)
  //     });

  //     if (!response.ok) {
  //       throw new Error('Failed to update registrant');
  //     }

  //     setRegistrations(prev => prev.map(r =>
  //       (r.unique_id || r.id) === registrantId ? { ...editingRegistrant } : r
  //     ));
  //     setEditRegistrantDialogOpen(false);
  //     setEditingRegistrant(null);
  //   } catch (err) {
  //     console.error('Error updating registrant:', err);
  //     setError('Failed to update registrant');
  //   }
  // }, [editingRegistrant]);
  const handleEditRegistrant = useCallback(async () => {
    if (!editingRegistrant) return;

    const registrantId = editingRegistrant.unique_id || editingRegistrant.id;
    const token = localStorage.getItem('token');

    try {
      // Ensure we are sending a clean, flat object to be stringified
      const response = await fetch(`${API_ENDPOINTS.registrations}/${registrantId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(editingRegistrant) 
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to update');
      }

      // Update local state ONLY if the server confirmed success
      setRegistrations(prev => prev.map(r =>
        (r.unique_id || r.id) === registrantId ? { ...editingRegistrant } : r
      ));
      
      setEditRegistrantDialogOpen(false);
      setEditingRegistrant(null);
    } catch (err) {
      console.error('Update failed:', err);
    }
}, [editingRegistrant, setRegistrations]);

  // Delete registrant with confirmation
  const handleDeleteRegistrant = useCallback(async (registrantId) => {
    if (!registrantId || !window.confirm('Are you sure you want to remove this registrant?')) {
      return;
    }

    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_ENDPOINTS.registrations}/${registrantId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to delete from server');
      }

      setRegistrations(prev => prev.filter(r => (r.unique_id || r.id) !== registrantId));

    } catch (err) {
      console.error('Error removing registrant:', err);
      setError(err.message || 'Failed to remove registrant');
    }
  }, [setRegistrations]);

  const openEditRegistrantDialog = useCallback((registrant) => {
    if (registrant) {
      setEditingRegistrant({ ...registrant });
      setEditRegistrantDialogOpen(true);
    }
  }, []);

  const handleContactClick = useCallback((event, registrant, type) => {
    if (registrant && type) {
      setContactAnchorEl(event.currentTarget);
      setActiveContact(registrant);
      setContactType(type);
    }
  }, []);

  const handleCloseContactPopover = useCallback(() => {
    setContactAnchorEl(null);
    setActiveContact(null);
    setContactType(null);
  }, []);

  const handleExportToExcel = useCallback(() => {
    if (!selectedEvent || filteredRegistrations.length === 0) return;

    const sections = [
      {
        title: 'PARTICIPANT INFORMATION',
        columns: ['Full Legal Name', 'Preferred Name', 'Date of Birth', 'Age', 'Gender', 'Pronouns', 'Email', 'Phone Number'],
        getData: (reg) => [
          reg.fullName || '', reg.preferredName || '',
          reg.dateOfBirth ? new Date(reg.dateOfBirth).toLocaleDateString() : '',
          calculateAge(reg.dateOfBirth) || '', reg.gender || '',
          getPronouns(reg.prounouns) || '', reg.dancerEmail || '', reg.dancerPhoneNumber || ''
        ]
      },
      {
        title: 'GUARDIAN INFORMATION',
        columns: ['Full Name', 'Email', 'Phone Number'],
        getData: (reg) => [reg.guardianFullName || '', reg.guardianEmail || '', reg.guardianPhoneNumber || '']
      },
      {
        title: 'EMERGENCY CONTACT',
        columns: ['Name', 'Relationship', 'Phone', 'Email'],
        getData: (reg) => [
          reg.emergencyContactName || '', reg.emergencyContactRelationship || '',
          reg.emergencyContactPhoneNumber || '', reg.emergencyContactEmail || ''
        ]
      },
      {
        title: 'RDA AFFILIATION',
        columns: ['Member/Alumni', 'Region', 'Dance School/Company'],
        getData: (reg) => [
          reg.RDAMember || '',
          reg.RDARegion ? (reg.RDARegion.label || reg.RDARegion) : '',
          reg.currentRDADanceSchoolCompany ? (reg.currentRDADanceSchoolCompany.label || reg.currentRDADanceSchoolCompany) : ''
        ]
      },
      {
        title: 'MERCHANDISE (DCI)',
        columns: ['T-Shirt Qty', 'Sweatshirt Qty'],
        getData: (reg) => [
          reg['Dance & Choreography Intensive T-Shirt ($30 each)Quantity'] ?? '',
          reg['Dance & Choreography Intensive Sweatshirt ($50 each)Quantity'] ?? ''
        ]
      },
      {
        title: 'MERCHANDISE (PW)',
        columns: ['T-Shirt Qty', 'Sweatshirt Qty'],
        getData: (reg) => [
          reg['Preformance Workshop T-Shirt ($30 each)Quantity'] ?? '',
          reg['Preformance Workshop Sweatshirt ($50 each)Quantity'] ?? ''
        ]
      },
      {
        title: 'AUTHORIZED PICKUP',
        columns: ['Name', 'Relationship', 'Phone', 'Email'],
        getData: (reg) => [
          reg.authorizedName1 || '', reg.authorizedRelationship1 || '',
          reg.authorizedPhone1 || '', reg.authorizedEmail1 || ''
        ]
      },
      {
        title: 'DCI OBSERVER PASSES & TICKETS',
        columns: ['Full Day Passes', 'Choreography Passes', 'Guest Tickets'],
        getData: (reg) => [reg.fullDayPasses || '', reg.choreographyPasses || '', reg.guestTickets || '']
      },
      {
        title: 'PROGRAM',
        columns: ['Role'],
        getData: (reg) => [reg[selectedEventId] || '']
      }
    ];

    // Build group header row and column header row
    const groupRow = [];
    const headerRow = [];
    sections.forEach((section) => {
      groupRow.push(section.title);
      headerRow.push(...section.columns);
      for (let i = 1; i < section.columns.length; i++) {
        groupRow.push('');
      }
    });

    // Build data rows
    const dataRows = filteredRegistrations.map(reg => {
      const row = [];
      sections.forEach(section => row.push(...section.getData(reg)));
      return row;
    });

    // Assemble: title, subtitle, blank, group headers, column headers, data
    const titleRow = [`${selectedEvent.title} \u2014 ${selectedEvent.location} \u2014 ${selectedEvent.date}`];
    const subtitleRow = [`Registrations: ${filteredRegistrations.length}  |  Exported: ${new Date().toLocaleDateString()}`];
    const wsData = [titleRow, subtitleRow, [], groupRow, headerRow, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-fit column widths
    const totalCols = headerRow.length;
    const colWidths = [];
    for (let c = 0; c < totalCols; c++) {
      let maxLen = (headerRow[c] || '').length;
      dataRows.forEach(row => {
        const val = String(row[c] ?? '');
        if (val.length > maxLen) maxLen = val.length;
      });
      colWidths.push({ wch: Math.min(Math.max(maxLen + 2, 10), 30) });
    }
    ws['!cols'] = colWidths;

    // Merge section group headers across their column spans
    const merges = [];
    let colOffset = 0;
    sections.forEach((section) => {
      const span = section.columns.length;
      if (span > 1) {
        merges.push({ s: { r: 3, c: colOffset }, e: { r: 3, c: colOffset + span - 1 } });
      }
      colOffset += span;
    });
    // Merge title and subtitle rows across all columns
    if (totalCols > 1) {
      merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } });
      merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } });
    }
    ws['!merges'] = merges;

    // Row heights for readability
    ws['!rows'] = [
      { hpt: 24 },  // title
      { hpt: 18 },  // subtitle
      { hpt: 8 },   // spacer
      { hpt: 20 },  // section groups
      { hpt: 18 }   // column headers
    ];

    const wb = XLSX.utils.book_new();
    const sheetName = (selectedEvent.title.length > 31
      ? selectedEvent.title.substring(0, 31)
      : selectedEvent.title).replace(/[\\/*?[\]:]/g, '');
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const safeEventId = selectedEvent.id.replace(/[^a-zA-Z0-9-_]/g, '_');
    XLSX.writeFile(wb, `${safeEventId}_Registrations_${new Date().toISOString().split('T')[0]}.xlsx`);
  }, [selectedEvent, filteredRegistrations, selectedEventId]);

  

  return (
    <Box sx={{ display: 'flex', bgcolor: COLORS.bg, minHeight: '100vh', color: COLORS.textPrimary }}>

      {/* LEFT SIDEBAR - Events Navigation */}
      <Box
        component="nav"
        aria-label="Events navigation"
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          width: SIDEBAR_WIDTH,
          height: '100vh',
          bgcolor: COLORS.sidebar,
          borderRight: `1px solid ${COLORS.border}`,
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1000
        }}
      >
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${COLORS.border}` }}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
            <EventIcon sx={{ color: COLORS.accent, fontSize: 22 }} aria-hidden="true" />
            <Typography variant="h6" component="h1" fontWeight={700} fontSize="1rem">RDA Events</Typography>
          </Stack>
          <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
            2026 Season
          </Typography>

          

        </Box>

        {/* Events List */}
        <Box component="ul" sx={{ flex: 1, overflow: 'auto', py: 1, m: 0, p: 0, listStyle: 'none' }}>
          {eventMetadata.map((event) => (
            <Box
              component="li"
              key={event.id}
              onClick={() => setSelectedEventId(event.id)}
              onKeyDown={(e) => e.key === 'Enter' && setSelectedEventId(event.id)}
              role="button"
              tabIndex={0}
              aria-pressed={selectedEventId === event.id}
              sx={{
                px: 2.5,
                py: 2,
                cursor: 'pointer',
                bgcolor: selectedEventId === event.id ? 'rgba(108, 93, 211, 0.15)' : 'transparent',
                borderLeft: selectedEventId === event.id ? `3px solid ${COLORS.accent}` : '3px solid transparent',
                '&:hover': {
                  bgcolor: selectedEventId === event.id ? 'rgba(108, 93, 211, 0.2)' : 'rgba(255,255,255,0.02)'
                },
                '&:focus-visible': {
                  outline: `2px solid ${COLORS.accent}`,
                  outlineOffset: -2
                }
              }}
            >
              <Typography
                variant="body2"
                fontWeight={selectedEventId === event.id ? 600 : 400}
                sx={{
                  color: selectedEventId === event.id ? COLORS.accent : COLORS.textPrimary,
                  fontSize: '0.85rem',
                  mb: 0.5
                }}
              >
                {event.title}
              </Typography>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: '0.75rem', display: 'block' }}>
                {event.location}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" mt={0.5}>
                <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: '0.7rem' }}>
                  {event.date}
                </Typography>
                <Chip
                  label={getEventCount(event.id)}
                  size="small"
                  aria-label={`${getEventCount(event.id)} registrations`}
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    bgcolor: getEventCount(event.id) > 0 ? 'rgba(52, 199, 89, 0.1)' : 'rgba(255,255,255,0.05)',
                    color: getEventCount(event.id) > 0 ? COLORS.success : COLORS.textSecondary,
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 0.8 }
                  }}
                />
              </Stack>
            </Box>
          ))}
        </Box>
        <Divider sx={{color: COLORS.accent}}/>
          <Button 
  component={Link} 
  to="/businessHome" 
  variant="outlined"
  sx={{ borderColor: '#302C3F', color: '#F8F5FE' }}
>
  Return to Home
</Button>
      </Box>

      {/* MAIN CONTENT AREA */}
      <Box component="main" sx={{ flex: 1, ml: `${SIDEBAR_WIDTH}px`, p: 3 }}>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }} role="status" aria-label="Loading">
            <CircularProgress sx={{ color: COLORS.accent }} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: '10px' }} role="alert">
            Error loading registrations: {error}. Please check that the backend server is running.
          </Alert>
        )}

        {!loading && !error && (
          <>
            {!selectedEvent ? (
              /* All Events Dashboard View */
              <Box>
                <Typography variant="h4" component="h2" fontWeight={700} sx={{ mb: 1, color: COLORS.textPrimary }}>
                  Event Dashboard
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 4 }}>
                  Select an event from the sidebar to view its registration roster.
                </Typography>

                {/* Event Cards Grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 2 }}>
                  {eventMetadata.map((event) => (
                    <Paper
                      key={event.id}
                      onClick={() => setSelectedEventId(event.id)}
                      onKeyDown={(e) => e.key === 'Enter' && setSelectedEventId(event.id)}
                      role="button"
                      tabIndex={0}
                      sx={{
                        bgcolor: COLORS.card,
                        border: `1px solid ${COLORS.border}`,
                        borderRadius: '12px',
                        p: 2.5,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: COLORS.cardHover,
                          borderColor: COLORS.accent,
                          transform: 'translateY(-2px)'
                        },
                        '&:focus-visible': {
                          outline: `2px solid ${COLORS.accent}`,
                          outlineOffset: 2
                        }
                      }}
                    >
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                        <EventIcon sx={{ color: COLORS.accent, fontSize: 28 }} aria-hidden="true" />
                        <Chip
                          label={`${getEventCount(event.id)} registered`}
                          size="small"
                          sx={{
                            bgcolor: getEventCount(event.id) > 0 ? 'rgba(52, 199, 89, 0.1)' : 'rgba(255,255,255,0.05)',
                            color: getEventCount(event.id) > 0 ? COLORS.success : COLORS.textSecondary,
                            fontWeight: 600,
                            fontSize: '0.7rem'
                          }}
                        />
                      </Stack>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5, color: COLORS.textPrimary }}>
                        {event.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 0.5 }}>
                        {event.location}
                      </Typography>
                      <Typography variant="caption" sx={{ color: COLORS.accent }}>
                        {event.date}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>
            ) : (
              /* Selected Event Roster View */
              <Box>
                {/* Back Button */}
                <Button
                  startIcon={<ArrowBackIcon />}
                  onClick={() => setSelectedEventId(null)}
                  size="small"
                  sx={{
                    color: COLORS.textSecondary,
                    mb: 2,
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.05)', color: COLORS.textPrimary },
                    textTransform: 'none',
                    fontSize: '0.8rem'
                  }}
                >
                  Back to Dashboard
                </Button>

                {/* Event Header */}
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
                  <Box>
                    <Typography variant="h5" component="h2" fontWeight={700} sx={{ mb: 0.5, color: COLORS.textPrimary }}>
                      {selectedEvent.title}
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                      <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                        {selectedEvent.location}
                      </Typography>
                      <Chip
                        label={selectedEvent.date}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(108, 93, 211, 0.1)',
                          color: COLORS.accent,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 22
                        }}
                      />
                      <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
                        {filteredRegistrations.length} registration{filteredRegistrations.length !== 1 ? 's' : ''}
                      </Typography>
                    </Stack>
                  </Box>

                  <Stack direction="row" spacing={1.5}>
                    {/* <Button
                      variant="outlined"
                      startIcon={<PersonAddIcon />}
                      onClick={() => setAddRegistrantDialogOpen(true)}
                      size="small"
                      sx={{
                        color: COLORS.textPrimary,
                        borderColor: COLORS.border,
                        '&:hover': { borderColor: COLORS.accent, bgcolor: 'rgba(108, 93, 211, 0.1)' },
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem'
                      }}
                    >
                      Add Registrant
                    </Button> */}
                    <Button
                      variant="contained"
                      startIcon={<PrintIcon />}
                      onClick={handleExportToExcel}
                      disabled={filteredRegistrations.length === 0}
                      size="small"
                      sx={{
                        bgcolor: COLORS.success,
                        '&:hover': { bgcolor: '#2db84e' },
                        '&:disabled': { bgcolor: 'rgba(52, 199, 89, 0.3)', color: 'rgba(255,255,255,0.5)' },
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.8rem'
                      }}
                    >
                      Export Excel
                    </Button>
                  </Stack>
                </Stack>

                {/* Registrations Table */}
                {filteredRegistrations.length > 0 ? (
                  <TableContainer
                    component={Paper}
                    sx={{
                      bgcolor: COLORS.card,
                      borderRadius: '12px',
                      border: `1px solid ${COLORS.border}`,
                      boxShadow: 'none'
                    }}
                  >
                    <Table size="small" aria-label="Registrations table">
                      <TableHead>
                        <TableRow sx={{
                          '& th': {
                            color: COLORS.textSecondary,
                            borderBottom: `1px solid ${COLORS.border}`,
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            py: 1.5,
                            bgcolor: COLORS.sidebar
                          }
                        }}>
                          <TableCell>Name</TableCell>
                          <TableCell>Preferred Name</TableCell>
                          <TableCell>Age</TableCell>
                          <TableCell>Gender</TableCell>
                          <TableCell>Role</TableCell>
                          <TableCell>Contact</TableCell>
                          <TableCell>Emergency</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredRegistrations.map((registrant) => (
                          <TableRow
                            key={registrant.unique_id}
                            onClick={() => handleRegistrantClick(registrant)}
                            onKeyDown={(e) => e.key === 'Enter' && handleRegistrantClick(registrant)}
                            tabIndex={0}
                            sx={{
                              '& td': { color: COLORS.textPrimary, borderBottom: `1px solid ${COLORS.border}`, py: 1.5 },
                              '&:hover': { bgcolor: COLORS.cardHover },
                              cursor: 'pointer',
                              transition: 'background-color 0.15s ease',
                              '&:focus-visible': {
                                outline: `2px solid ${COLORS.accent}`,
                                outlineOffset: -2
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} fontSize="0.85rem">
                                {registrant.fullName || '-'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontSize="0.85rem">
                                {registrant.preferredName || '-'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontSize="0.85rem">
                                {calculateAge(registrant.dateOfBirth) || '-'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontSize="0.85rem">
                                {registrant.gender || '-'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Chip
                                label={registrant[selectedEventId] || '-'}
                                size="small"
                                sx={{
                                  bgcolor: registrant[selectedEventId] === 'Choreographer'
                                    ? 'rgba(255, 159, 10, 0.1)'
                                    : 'rgba(108, 93, 211, 0.1)',
                                  color: registrant[selectedEventId] === 'Choreographer'
                                    ? COLORS.warning
                                    : COLORS.accent,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 22
                                }}
                              />
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontSize="0.8rem">
                                {registrant.dancerEmail || '-'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>
                                {registrant.dancerPhoneNumber || '-'}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {registrant.emergencyContactName ? (
                                <Chip
                                  icon={<ContactPhoneIcon sx={{ fontSize: 14 }} />}
                                  label={String(registrant.emergencyContactName).split(' ')[0]}
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleContactClick(e, registrant, 'emergency'); }}
                                  sx={{
                                    bgcolor: 'rgba(52, 199, 89, 0.1)',
                                    color: COLORS.textSecondary,
                                    cursor: 'pointer',
                                    height: 24,
                                    fontSize: '0.7rem',
                                    '&:hover': { bgcolor: 'rgba(52, 199, 89, 0.2)' },
                                    '& .MuiChip-icon': { color: COLORS.success }
                                  }}
                                />
                              ) : (
                                <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontSize: '0.8rem' }}>-</Typography>
                              )}
                            </TableCell>

                            <TableCell align="right">
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); openEditRegistrantDialog(registrant); }}
                                  sx={{ color: COLORS.accent }}
                                  aria-label={`Edit ${registrant.fullName}`}
                                >
                                  <EditIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteRegistrant(registrant.id); }}
                                  sx={{ color: COLORS.error }}
                                  aria-label={`Delete ${registrant.fullName}`}
                                >
                                  <DeleteIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box sx={{
                    textAlign: 'center',
                    py: 6,
                    bgcolor: COLORS.card,
                    borderRadius: '12px',
                    border: `1px solid ${COLORS.border}`
                  }}>
                    <PersonAddIcon sx={{ fontSize: 40, color: COLORS.border, mb: 1.5 }} aria-hidden="true" />
                    <Typography variant="body1" sx={{ color: COLORS.textSecondary, mb: 1 }}>
                      No registrations for this event
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setAddRegistrantDialogOpen(true)}
                      size="small"
                      sx={{
                        color: COLORS.accent,
                        borderColor: COLORS.accent,
                        '&:hover': { bgcolor: 'rgba(108, 93, 211, 0.1)' },
                        borderRadius: '8px',
                        textTransform: 'none'
                      }}
                    >
                      Add Registrant
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Contact Details Popover */}
      <Popover
        open={Boolean(contactAnchorEl)}
        anchorEl={contactAnchorEl}
        onClose={handleCloseContactPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            bgcolor: COLORS.sidebar,
            color: COLORS.textPrimary,
            p: 2,
            borderRadius: '10px',
            border: `1px solid ${COLORS.accent}`,
            minWidth: 240
          }
        }}
      >
        {activeContact && contactType === 'guardian' && (
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <GuardianIcon sx={{ color: COLORS.accent, fontSize: 18 }} aria-hidden="true" />
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: COLORS.accent }}>
                Guardian
              </Typography>
            </Stack>
            <Box>
              <Typography variant="body2" fontWeight={500}>{activeContact.guardianFullName}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Phone</Typography>
              <Typography variant="body2">{activeContact.guardianPhoneNumber || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Email</Typography>
              <Typography variant="body2">{activeContact.guardianEmail || '-'}</Typography>
            </Box>
          </Stack>
        )}
        {activeContact && contactType === 'emergency' && (
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <ContactPhoneIcon sx={{ color: COLORS.success, fontSize: 18 }} aria-hidden="true" />
              <Typography variant="subtitle2" fontWeight={600} sx={{ color: COLORS.success }}>
                Emergency Contact
              </Typography>
            </Stack>
            <Box>
              <Typography variant="body2" fontWeight={500}>{activeContact.emergencyContactName}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>{activeContact.emergencyContactRelationship}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Phone</Typography>
              <Typography variant="body2">{activeContact.emergencyContactPhoneNumber || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary }}>Email</Typography>
              <Typography variant="body2">{activeContact.emergencyContactEmail || '-'}</Typography>
            </Box>
          </Stack>
        )}
      </Popover>

      {/* ADD REGISTRANT DIALOG */}
      <Dialog
        open={addRegistrantDialogOpen}
        onClose={() => setAddRegistrantDialogOpen(false)}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
        aria-labelledby="add-registrant-dialog-title"
        PaperProps={{
          sx: {
            bgcolor: COLORS.card,
            color: COLORS.textPrimary,
            borderRadius: '12px',
            border: `1px solid ${COLORS.border}`,
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle id="add-registrant-dialog-title" sx={{ borderBottom: `1px solid ${COLORS.border}`, pb: 2, flexShrink: 0 }}>
          <Typography variant="h6" component="span" fontWeight={700} fontSize="1rem" sx={{ color: COLORS.textPrimary }}>Add New Registrant</Typography>
          <Typography variant="caption" component="p" sx={{ color: COLORS.textSecondary }}>
            to {selectedEvent?.title} - {selectedEvent?.location}
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            overflowY: 'auto',
            flex: '1 1 auto',
            bgcolor: COLORS.card
          }}
        >
          <RegistrantForm registrant={newRegistrant} setRegistrant={setNewRegistrant} />
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
          <Button
            onClick={() => { setAddRegistrantDialogOpen(false); setNewRegistrant(createEmptyRegistrant()); }}
            sx={{ color: COLORS.textSecondary, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddRegistrant}
            variant="contained"
            disabled={!newRegistrant.fullName?.trim() || !newRegistrant.dateOfBirth}
            sx={{
              bgcolor: COLORS.accent,
              '&:hover': { bgcolor: COLORS.accentHover },
              '&:disabled': { bgcolor: 'rgba(108, 93, 211, 0.3)' },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Add Registrant
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT REGISTRANT DIALOG */}
      <Dialog
        open={editRegistrantDialogOpen}
        onClose={() => { setEditRegistrantDialogOpen(false); setEditingRegistrant(null); }}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
        aria-labelledby="edit-registrant-dialog-title"
        PaperProps={{
          sx: {
            bgcolor: COLORS.card,
            color: COLORS.textPrimary,
            borderRadius: '12px',
            border: `1px solid ${COLORS.border}`,
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle id="edit-registrant-dialog-title" sx={{ borderBottom: `1px solid ${COLORS.border}`, pb: 2, flexShrink: 0 }}>
          <Typography variant="h6" component="span" fontWeight={700} fontSize="1rem" sx={{ color: COLORS.textPrimary }}>Edit Registrant</Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: 2.5,
            overflowY: 'auto',
            flex: '1 1 auto',
            bgcolor: COLORS.card
          }}
        >
          {editingRegistrant && (
            <RegistrantForm registrant={editingRegistrant} setRegistrant={setEditingRegistrant} />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${COLORS.border}`, flexShrink: 0 }}>
          <Button
            onClick={() => { setEditRegistrantDialogOpen(false); setEditingRegistrant(null); }}
            sx={{ color: COLORS.textSecondary, textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditRegistrant}
            variant="contained"
            disabled={!editingRegistrant?.fullName?.trim() || !editingRegistrant?.dateOfBirth}
            sx={{
              bgcolor: COLORS.accent,
              '&:hover': { bgcolor: COLORS.accentHover },
              '&:disabled': { bgcolor: 'rgba(108, 93, 211, 0.3)' },
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      
    </Box>
  );
};

export default EventsDashboard